// Sections metrics table, ported from menu_test/sections/items_list.coffee: every metric shows
// a single column, respondent counts as a share bar and the rest as a formatted value.

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { processText } from '@aytm/app/stats/page/components/lib/utils'

import { formatSeconds } from '@aytm/app/stats/page/question-views/mn/lib/format'
import {
  buildHeaderTitle,
  calcSortedIndex,
  resolveClickable
} from '@aytm/app/stats/page/question-views/mn/lib/table-utils'
import {
  SECTION_PERCENT_CAPABLE_VIEWS,
  VIEW_MODES,
  purchasedCount
} from '@aytm/app/stats/page/question-views/mn/lib/viewOptions'

export const SECTION_RESPONSES_TITLES = {
  attention: 'Time',
  time: 'Time',
  timeToFirstClick: 'Time',
  pVolume: 'Quantity',
  pAmount: 'Amount'
}

const METRIC_COLUMN_WIDTH = '185px'
const SECONDS_VIEW_MODES = ['attention', 'time', 'timeToFirstClick']

const calcTraitTable = ({ id, viewMode, priceTemplate, trait, traitName, traitValues, answer, sectionPurchased }) => {
  const table = {
    id: `${id}-traits`,
    cols: [
      {
        type: 'title',
        title:
          (Object.hasOwn(trait, 'title') && trait.title) ||
          (Object.hasOwn(trait, 'id') && traitName.toUpperCase()) ||
          traitName
      }
    ],
    rows: traitValues.map((v, i) => {
      const a = answer[v.id] || {}
      const cells = [{ title: v.name, colorIndex: (i % 21) + 1 }]

      if (SECTION_PERCENT_CAPABLE_VIEWS.includes(viewMode)) {
        const percents =
          VIEW_MODES.PURCHASED === viewMode
            ? sectionPurchased
              ? (purchasedCount(a) / sectionPurchased) * 100
              : 0
            : a.percents || 0
        cells.push({ value: percents.toFixed(1) })
      }

      const value =
        VIEW_MODES.OPENED === viewMode
          ? a.amount || 0
          : VIEW_MODES.PURCHASED === viewMode
            ? purchasedCount(a)
            : a[viewMode] || 0

      cells.push({
        value:
          'pAmount' === viewMode && priceTemplate
            ? priceTemplate.replace(/{{price}}/gi, value.toFixed(2))
            : ['pAmount', 'pVolume'].includes(viewMode)
              ? value.toFixed(2)
              : (() => {
                  const formatTraitValue = formatAmounts({ amount: value }, 'amount', 1)
                  // zero stays unit-less in the trait table, matching the original behaviour
                  return value && SECONDS_VIEW_MODES.includes(viewMode)
                    ? formatSeconds(formatTraitValue)
                    : formatTraitValue
                })()
      })

      return { cells, position: i + 1 }
    }),
    classNames: ['child']
  }

  if (SECTION_PERCENT_CAPABLE_VIEWS.includes(viewMode)) table.cols.push({ type: 'value', title: '%' })
  table.cols.push({ type: 'value', title: SECTION_RESPONSES_TITLES[viewMode] || 'Respondents' })

  return table
}

export const calcSectionsTable = ({
  id,
  answer,
  sections = [],
  viewMode,
  priceTemplate,
  format,
  trait,
  traitName,
  traitValues = [],
  isTraitType,
  viewClickable,
  percentDecimals,
  sortOrder,
  onSort,
  onLabelClick
} = {}) => {
  const clickable = resolveClickable({ format, viewClickable })
  const isLoading = 'loading' === answer
  const headerTitle = buildHeaderTitle({ answer, format })
  const questionTotal = answer?.total || 0

  const props = {
    id,
    cols: [{ type: 'title', title: 'Sections', sort: (aRow, bRow, dir) => dir * (aRow.position - bRow.position) }],
    onSort
  }

  if (SECTION_RESPONSES_TITLES[viewMode]) {
    props.cols.push({
      type: 'value',
      title: headerTitle(SECTION_RESPONSES_TITLES[viewMode]),
      width: METRIC_COLUMN_WIDTH,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].value + aRow.position / 10000 - (bRow.cells[1].value + bRow.position / 10000)),
      format: value => {
        if (['pAmount', 'pVolume'].includes(viewMode)) {
          const formatted = value.toFixed(2)
          return 'pAmount' === viewMode && priceTemplate ? priceTemplate.replace(/{{price}}/gi, formatted) : formatted
        }
        if (SECONDS_VIEW_MODES.includes(viewMode)) return formatSeconds(formatAmounts({ amount: value }, 'amount', 1))
        return formatAmounts({ amount: value }, 'amount', 1)
      }
    })
  } else {
    // opened / purchased → respondents count + share bar
    props.cols.push({
      type: 'progress',
      title: headerTitle('Respondents'),
      width: METRIC_COLUMN_WIDTH,
      percentDecimals,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].percents + aRow.position / 10000 - (bRow.cells[1].percents + bRow.position / 10000))
    })
  }

  props.rows = sections.map(section => {
    const s = answer?.[`sq${section.position}`] || {}

    const row = {
      cells: [
        {
          title: processText(section.text),
          label: `SQ${section.position}`,
          colorIndex: trait && isTraitType ? 0 : ((section.position - 1) % 21) + 1
        }
      ],
      position: section.position
    }

    if (SECTION_RESPONSES_TITLES[viewMode]) {
      row.cells.push({ value: s[viewMode] || 0, loading: isLoading })
    } else if (VIEW_MODES.PURCHASED === viewMode) {
      const purchased = purchasedCount(s)
      row.cells.push({
        amount: purchased,
        percents: questionTotal ? (purchased / questionTotal) * 100 : 0,
        loading: isLoading
      })
    } else {
      row.cells.push({ amount: s.amount || 0, percents: s.percents || 0, loading: isLoading })
    }

    if (clickable) row.cells[0].onLabelClick = onLabelClick

    if (trait && isTraitType) {
      const ta = s[traitName]
      if (undefined !== ta) {
        row.table = calcTraitTable({
          id,
          viewMode,
          priceTemplate,
          trait,
          traitName,
          traitValues,
          answer: ta,
          sectionPurchased: purchasedCount(s)
        })
      }
    }

    return row
  })

  calcSortedIndex(props.cols, sortOrder)

  return props
}
